import { FormattedMessage } from 'react-intl';

import { Button } from '~components/generic/editor/Button/Button';
import { Chip } from '~components/generic/editor/Chip/Chip';
import { Select } from '~components/generic/editor/Select/Select';
import { UserSearchAutocomplete } from '~components/generic/UserSearchAutocomplete/UserSearchAutocomplete';
import { CheckIcon, SaveIcon, UserAddIcon, WarningIcon } from '~components/icons';
import { Table, TableDataShape } from './Table';
import { TablePlaceholder } from './TablePlaceholder';
import styles from './PermissionsTable.module.css';

const ScopeItems = [
  {
    value: 'inherit',
    label: 'optionScopeInherit',
  },
  {
    value: 'subtree',
    label: 'optionScopeSubtree',
  },
  {
    value: 'node',
    label: 'optionScopeNode',
  },
];

export type PermissionsTableProps = {
  isLoading: boolean;
  isUpdating: boolean;
  isProcessing: boolean;
  hasChanges: boolean;
  data: TableDataShape;
  onUpdate: (rowIndex: number, columnId: string, value: boolean) => void;
  onReset: () => void;
  onSave: () => void;
  onChangeScope: (scope: string) => void;
  scope: string;
  nodeName: string;
  updateSuccess?: boolean;
  onAddUser: (username: string, id: number) => void;
};

export const PermissionsTable = ({
  isLoading,
  isProcessing,
  isUpdating,
  data,
  onUpdate,
  hasChanges,
  onReset,
  onSave,
  onChangeScope,
  scope,
  nodeName,
  updateSuccess,
  onAddUser,
}: PermissionsTableProps) => {
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          <FormattedMessage id="hlManagePermissions" defaultMessage="Manage Permissions" />
        </div>
        <div className={styles.name}>{nodeName}</div>
        <div className={styles.scope}>
          <Select
            items={ScopeItems}
            selectedValue={scope}
            disabled={isLoading || isProcessing}
            onChange={onChangeScope}
          />
        </div>
      </div>
      <div className={styles.table}>
        {isLoading ? (
          <TablePlaceholder />
        ) : (
          <Table data={data} canChange={scope !== 'inherit'} updateData={onUpdate} disabled={isUpdating} />
        )}
      </div>
      <div className={styles.newUser}>
        <UserSearchAutocomplete
          onSelect={onAddUser}
          disabled={scope === 'inherit'}
          icon={<UserAddIcon />}
          placeholderText="placeholderAddUser"
        />
      </div>
      <div className={styles.footer}>
        {updateSuccess === true && (
          <div className={styles.footerAction}>
            <FormattedMessage id="msgPermissionsSaveSuccessful" defaultMessage="Saved!">
              {(msg: any) => <Chip label={msg} status="success" preIcon={<CheckIcon />} />}
            </FormattedMessage>
          </div>
        )}
        {updateSuccess === false && (
          <div className={styles.footerAction}>
            <FormattedMessage id="msgPermissionsSaveFailed" defaultMessage="Error!">
              {(msg: any) => <Chip label={msg} status="error" preIcon={<WarningIcon />} />}
            </FormattedMessage>
          </div>
        )}
        <div className={styles.footerAction}>
          <FormattedMessage id="btnResetPermissions" defaultMessage="Discard Changes">
            {(msg: any) => <Button onClick={onReset} disabled={!hasChanges || isProcessing} label={msg} />}
          </FormattedMessage>
        </div>
        <div className={styles.footerAction}>
          <FormattedMessage id="btnSavePermissions" defaultMessage="Save">
            {(msg: any) => (
              <Button
                onClick={onSave}
                disabled={!hasChanges}
                processing={isProcessing}
                label={msg}
                icon={<SaveIcon />}
              />
            )}
          </FormattedMessage>
        </div>
      </div>
    </div>
  );
};
